import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Scan, LogOut, Download, Plus, Trash2, Users, ShoppingCart, Home, Package } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import PhotoAnalyzer from '../components/PhotoAnalyzer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, logout }) => {
  const [rooms, setRooms] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [familyGroup, setFamilyGroup] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [activeTab, setActiveTab] = useState('inventory');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Modal states
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPhotoAnalyzer, setShowPhotoAnalyzer] = useState(false);
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);

  // Form states
  const [roomName, setRoomName] = useState('');
  const [roomColor, setRoomColor] = useState('#10b981');
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState('cupboard');
  const [itemName, setItemName] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemBarcode, setItemBarcode] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [shoppingItemName, setShoppingItemName] = useState('');
  const [shoppingItemQuantity, setShoppingItemQuantity] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roomsRes, locationsRes, itemsRes, shoppingRes, familyRes, membersRes, countRes] = await Promise.all([
        axios.get(`${API}/rooms`),
        axios.get(`${API}/storage-locations`),
        axios.get(`${API}/items`),
        axios.get(`${API}/shopping-list`),
        axios.get(`${API}/family/group`),
        axios.get(`${API}/family/members`),
        axios.get(`${API}/items/count`)
      ]);

      setRooms(roomsRes.data);
      setStorageLocations(locationsRes.data);
      setItems(itemsRes.data);
      setShoppingList(shoppingRes.data);
      setFamilyGroup(familyRes.data);
      setFamilyMembers(membersRes.data);
      setItemCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleAddRoom = async () => {
    if (!roomName) {
      toast.error('Please enter a room name');
      return;
    }

    try {
      await axios.post(`${API}/rooms`, {
        name: roomName,
        color_tab: roomColor
      });
      toast.success('Room added!');
      setShowAddRoom(false);
      setRoomName('');
      setRoomColor('#10b981');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add room');
    }
  };

  const handleAddLocation = async () => {
    if (!locationName || !selectedRoom) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/storage-locations`, {
        room_id: selectedRoom,
        name: locationName,
        location_type: locationType
      });
      toast.success('Storage location added!');
      setShowAddLocation(false);
      setLocationName('');
      setLocationType('cupboard');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    }
  };

  const handleAddItem = async () => {
    if (!itemName || !selectedLocation) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      await axios.post(`${API}/items`, {
        storage_location_id: selectedLocation,
        name: itemName,
        barcode: itemBarcode || null,
        quantity: itemQuantity
      });
      toast.success('Item added!');
      setShowAddItem(false);
      setItemName('');
      setItemQuantity(1);
      setItemBarcode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleBarcodeScanned = (barcode) => {
    setItemBarcode(barcode);
    setShowScanner(false);
    toast.success(`Barcode scanned: ${barcode}`);
  };

  const handlePhotoAnalyzed = async (analyzedItems) => {
    if (!selectedLocation) {
      toast.error('Please select a storage location first');
      return;
    }

    try {
      for (const item of analyzedItems) {
        await axios.post(`${API}/items`, {
          storage_location_id: selectedLocation,
          name: item.name,
          quantity: item.quantity || 1
        });
      }
      toast.success(`Added ${analyzedItems.length} items!`);
      setShowPhotoAnalyzer(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add analyzed items');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/items/${itemId}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'shelfie_inventory.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported!');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleAddShoppingItem = async () => {
    if (!shoppingItemName) {
      toast.error('Please enter item name');
      return;
    }

    try {
      await axios.post(`${API}/shopping-list`, {
        item_name: shoppingItemName,
        quantity: shoppingItemQuantity
      });
      toast.success('Added to shopping list');
      setShoppingItemName('');
      setShoppingItemQuantity(1);
      fetchData();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleToggleShoppingItem = async (itemId) => {
    try {
      await axios.put(`${API}/shopping-list/${itemId}/toggle`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle item');
    }
  };

  const handleDeleteShoppingItem = async (itemId) => {
    try {
      await axios.delete(`${API}/shopping-list/${itemId}`);
      toast.success('Item removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleClearCompleted = async () => {
    try {
      await axios.delete(`${API}/shopping-list/completed/clear`);
      toast.success('Completed items cleared');
      fetchData();
    } catch (error) {
      toast.error('Failed to clear items');
    }
  };

  const handleCreateFamily = async () => {
    if (!familyName) {
      toast.error('Please enter family name');
      return;
    }

    try {
      await axios.post(`${API}/family/create`, { name: familyName });
      toast.success('Family group created!');
      setFamilyName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    }
  };

  const handleJoinFamily = async () => {
    if (!inviteCode) {
      toast.error('Please enter invite code');
      return;
    }

    try {
      await axios.post(`${API}/family/join/${inviteCode}`);
      toast.success('Joined family group!');
      setInviteCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join family');
    }
  };

  const handleLeaveFamily = async () => {
    try {
      await axios.delete(`${API}/family/leave`);
      toast.success('Left family group');
      fetchData();
    } catch (error) {
      toast.error('Failed to leave family');
    }
  };

  const filteredLocations = selectedRoom
    ? storageLocations.filter(loc => loc.room_id === selectedRoom)
    : storageLocations;

  const filteredItems = selectedLocation
    ? items.filter(item => item.storage_location_id === selectedLocation)
    : items;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <nav className="backdrop-blur-md bg-white/70 border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Scan className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Shelfie</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className="w-8 h-8 rounded-full" />
              <span className="text-sm font-medium text-gray-700">{user.name}</span>
            </div>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              data-testid="export-csv-button"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={logout}
              variant="ghost"
              data-testid="logout-button"
              className="text-gray-600 hover:text-gray-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard icon={<Home />} label="Rooms" value={rooms.length} color="from-emerald-500 to-teal-600" />
          <StatCard icon={<Package />} label="Storage Locations" value={storageLocations.length} color="from-teal-500 to-cyan-600" />
          <StatCard icon={<Scan />} label="Items" value={itemCount} color="from-cyan-500 to-blue-600" />
          <StatCard icon={<ShoppingCart />} label="Shopping List" value={shoppingList.filter(i => !i.completed).length} color="from-blue-500 to-indigo-600" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-white/80 backdrop-blur-sm">
            <TabsTrigger value="inventory" data-testid="inventory-tab">Inventory</TabsTrigger>
            <TabsTrigger value="shopping" data-testid="shopping-tab">Shopping List</TabsTrigger>
            <TabsTrigger value="family" data-testid="family-tab">Family</TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-6">
            {/* Room Selection */}
            <div className="glass rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Rooms</h2>
                <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-room-button" className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Room
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-room-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Room</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="roomName">Room Name</Label>
                        <Input
                          id="roomName"
                          data-testid="room-name-input"
                          value={roomName}
                          onChange={(e) => setRoomName(e.target.value)}
                          placeholder="e.g., Kitchen, Bedroom"
                        />
                      </div>
                      <div>
                        <Label htmlFor="roomColor">Color Tab</Label>
                        <Input
                          id="roomColor"
                          type="color"
                          data-testid="room-color-input"
                          value={roomColor}
                          onChange={(e) => setRoomColor(e.target.value)}
                        />
                      </div>
                      <Button onClick={handleAddRoom} data-testid="save-room-button" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                        Save Room
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {rooms.map(room => (
                  <button
                    key={room.id}
                    data-testid={`room-${room.id}`}
                    onClick={() => setSelectedRoom(selectedRoom === room.id ? null : room.id)}
                    className={`p-4 rounded-xl font-semibold transition-all ${
                      selectedRoom === room.id
                        ? 'ring-4 ring-offset-2 ring-emerald-500 shadow-lg transform scale-105'
                        : 'shadow hover:shadow-lg'
                    }`}
                    style={{ backgroundColor: room.color_tab, color: 'white' }}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Storage Locations */}
            {selectedRoom && (
              <div className="glass rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Storage Locations</h2>
                  <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-location-button" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Location
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-location-dialog">
                      <DialogHeader>
                        <DialogTitle>Add Storage Location</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="locationName">Location Name</Label>
                          <Input
                            id="locationName"
                            data-testid="location-name-input"
                            value={locationName}
                            onChange={(e) => setLocationName(e.target.value)}
                            placeholder="e.g., Top Shelf, Main Fridge"
                          />
                        </div>
                        <div>
                          <Label htmlFor="locationType">Type</Label>
                          <Select value={locationType} onValueChange={setLocationType}>
                            <SelectTrigger data-testid="location-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cupboard">Cupboard</SelectItem>
                              <SelectItem value="fridge">Fridge</SelectItem>
                              <SelectItem value="freezer">Freezer</SelectItem>
                              <SelectItem value="drawer">Drawer</SelectItem>
                              <SelectItem value="pantry">Pantry</SelectItem>
                              <SelectItem value="shelf">Shelf</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAddLocation} data-testid="save-location-button" className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                          Save Location
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {filteredLocations.map(location => (
                    <button
                      key={location.id}
                      data-testid={`location-${location.id}`}
                      onClick={() => setSelectedLocation(selectedLocation === location.id ? null : location.id)}
                      className={`p-4 rounded-xl bg-white border-2 transition-all ${
                        selectedLocation === location.id
                          ? 'border-teal-500 shadow-lg transform scale-105'
                          : 'border-gray-200 hover:border-teal-300 hover:shadow'
                      }`}
                    >
                      <div className="font-semibold text-gray-800">{location.name}</div>
                      <div className="text-sm text-gray-500 capitalize">{location.location_type}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            {selectedLocation && (
              <div className="glass rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Items</h2>
                  <div className="flex gap-2">
                    <Dialog open={showScanner} onOpenChange={setShowScanner}>
                      <DialogTrigger asChild>
                        <Button data-testid="scan-barcode-button" variant="outline" className="border-cyan-500 text-cyan-600">
                          <Scan className="w-4 h-4 mr-2" />
                          Scan Barcode
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="barcode-scanner-dialog">
                        <DialogHeader>
                          <DialogTitle>Scan Barcode</DialogTitle>
                        </DialogHeader>
                        <BarcodeScanner onScanSuccess={handleBarcodeScanned} />
                      </DialogContent>
                    </Dialog>
                    <Dialog open={showPhotoAnalyzer} onOpenChange={setShowPhotoAnalyzer}>
                      <DialogTrigger asChild>
                        <Button data-testid="analyze-photo-button" variant="outline" className="border-blue-500 text-blue-600">
                          Analyze Photo
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="photo-analyzer-dialog">
                        <DialogHeader>
                          <DialogTitle>Analyze Photo</DialogTitle>
                        </DialogHeader>
                        <PhotoAnalyzer onAnalysisComplete={handlePhotoAnalyzed} />
                      </DialogContent>
                    </Dialog>
                    <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-item-button" className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Item
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="add-item-dialog">
                        <DialogHeader>
                          <DialogTitle>Add Item</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="itemName">Item Name *</Label>
                            <Input
                              id="itemName"
                              data-testid="item-name-input"
                              value={itemName}
                              onChange={(e) => setItemName(e.target.value)}
                              placeholder="e.g., Milk, Pasta"
                            />
                          </div>
                          <div>
                            <Label htmlFor="itemBarcode">Barcode (optional)</Label>
                            <Input
                              id="itemBarcode"
                              data-testid="item-barcode-input"
                              value={itemBarcode}
                              onChange={(e) => setItemBarcode(e.target.value)}
                              placeholder="Scan or enter barcode"
                            />
                          </div>
                          <div>
                            <Label htmlFor="itemQuantity">Quantity</Label>
                            <Input
                              id="itemQuantity"
                              type="number"
                              data-testid="item-quantity-input"
                              value={itemQuantity}
                              onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                              min="1"
                            />
                          </div>
                          <Button onClick={handleAddItem} data-testid="save-item-button" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                            Save Item
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="space-y-2">
                  {filteredItems.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No items yet. Add your first item!</p>
                  ) : (
                    filteredItems.map(item => (
                      <div key={item.id} data-testid={`item-${item.id}`} className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-cyan-300 transition-colors">
                        <div>
                          <div className="font-semibold text-gray-800">{item.name}</div>
                          <div className="text-sm text-gray-500">
                            Quantity: {item.quantity}
                            {item.barcode && ` • Barcode: ${item.barcode}`}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDeleteItem(item.id)}
                          data-testid={`delete-item-${item.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Shopping List Tab */}
          <TabsContent value="shopping" data-testid="shopping-content">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Shopping List</h2>
              <div className="flex gap-2 mb-6">
                <Input
                  data-testid="shopping-item-input"
                  value={shoppingItemName}
                  onChange={(e) => setShoppingItemName(e.target.value)}
                  placeholder="Add item to shopping list"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddShoppingItem()}
                />
                <Input
                  type="number"
                  data-testid="shopping-quantity-input"
                  value={shoppingItemQuantity}
                  onChange={(e) => setShoppingItemQuantity(parseInt(e.target.value) || 1)}
                  className="w-24"
                  min="1"
                />
                <Button onClick={handleAddShoppingItem} data-testid="add-shopping-item-button" className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {shoppingList.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Your shopping list is empty</p>
                ) : (
                  <>
                    {shoppingList.map(item => (
                      <div key={item.id} data-testid={`shopping-item-${item.id}`} className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            data-testid={`toggle-shopping-${item.id}`}
                            checked={item.completed}
                            onChange={() => handleToggleShoppingItem(item.id)}
                            className="w-5 h-5 text-emerald-500 rounded"
                          />
                          <div className={item.completed ? 'line-through text-gray-400' : 'text-gray-800'}>
                            <div className="font-semibold">{item.item_name}</div>
                            <div className="text-sm">Quantity: {item.quantity}</div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDeleteShoppingItem(item.id)}
                          data-testid={`delete-shopping-${item.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {shoppingList.some(i => i.completed) && (
                      <Button onClick={handleClearCompleted} data-testid="clear-completed-button" variant="outline" className="w-full mt-4">
                        Clear Completed Items
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Family Tab */}
          <TabsContent value="family" data-testid="family-content">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Family Sharing</h2>
              {!familyGroup ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-3">Create a Family Group</h3>
                    <div className="flex gap-2">
                      <Input
                        data-testid="family-name-input"
                        value={familyName}
                        onChange={(e) => setFamilyName(e.target.value)}
                        placeholder="Family name"
                      />
                      <Button onClick={handleCreateFamily} data-testid="create-family-button" className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                        Create
                      </Button>
                    </div>
                  </div>
                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-3">Join a Family Group</h3>
                    <div className="flex gap-2">
                      <Input
                        data-testid="invite-code-input"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        placeholder="Enter invite code"
                      />
                      <Button onClick={handleJoinFamily} data-testid="join-family-button" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                        Join
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h3 className="font-bold text-lg mb-2">{familyGroup.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">Invite Code: <span className="font-mono font-bold">{familyGroup.invite_code}</span></p>
                    <p className="text-xs text-gray-500">Share this code with family members to invite them</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3">Family Members ({familyMembers.length})</h3>
                    <div className="space-y-2">
                      {familyMembers.map(member => (
                        <div key={member.id} data-testid={`family-member-${member.id}`} className="flex justify-between items-center p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            <img src={member.user.picture || 'https://via.placeholder.com/40'} alt={member.user.name} className="w-10 h-10 rounded-full" />
                            <div>
                              <div className="font-semibold">{member.user.name}</div>
                              <div className="text-sm text-gray-500 capitalize">{member.role}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleLeaveFamily} data-testid="leave-family-button" variant="outline" className="w-full border-red-500 text-red-600 hover:bg-red-50">
                    Leave Family Group
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div className="glass rounded-xl p-6 hover:shadow-xl transition-all">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-sm mb-1">{label}</p>
        <p className="text-3xl font-bold text-gray-800">{value}</p>
      </div>
      <div className={`w-14 h-14 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center text-white`}>
        {icon}
      </div>
    </div>
  </div>
);

export default Dashboard;
